import {
  centerFeatureOf,
  mainFeatureOf,
  midpointFeatureCollection,
  vertexFeatureCollection,
} from "./features";

const ANNOTATION_SOURCE_PREFIX = "fs-";
const FILL_SUFFIX = "-fill";
const LINE_SUFFIX = "-line";
const LINE_HIT_SUFFIX = "-line-hit";
const LABEL_SUFFIX = "-label";
const VERTICES_SUFFIX = "-vertices";
const MIDPOINTS_SUFFIX = "-midpoints";
const CIRCLE_SUFFIX = "-circle";
const CENTER_SUFFIX = "-center";

const BODY_LAYER_SUFFIXES = [FILL_SUFFIX, LINE_HIT_SUFFIX, CIRCLE_SUFFIX];
const HANDLE_LAYER_SUFFIXES = [VERTICES_SUFFIX, MIDPOINTS_SUFFIX];
const TOPMOST_UI_SUFFIXES = [...BODY_LAYER_SUFFIXES, ...HANDLE_LAYER_SUFFIXES];

export const annotationSourceId = (id: number | string) =>
  `${ANNOTATION_SOURCE_PREFIX}${id}`;

export const lineLayerId = (sourceId: string) => `${sourceId}${LINE_SUFFIX}`;
export const hitLayerId = (sourceId: string) => `${sourceId}${LINE_HIT_SUFFIX}`;
export const fillLayerId = (sourceId: string) => `${sourceId}${FILL_SUFFIX}`;
export const labelLayerId = (sourceId: string) => `${sourceId}${LABEL_SUFFIX}`;
export const verticesLayerId = (sourceId: string) =>
  `${sourceId}${VERTICES_SUFFIX}`;
export const midpointsLayerId = (sourceId: string) =>
  `${sourceId}${MIDPOINTS_SUFFIX}`;
export const circleLayerId = (sourceId: string) =>
  `${sourceId}${CIRCLE_SUFFIX}`;
export const centerSourceId = (sourceId: string) =>
  `${sourceId}${CENTER_SUFFIX}`;

export const defaultVisibilityLayerIds = (sourceId: string) => [
  fillLayerId(sourceId),
  lineLayerId(sourceId),
  hitLayerId(sourceId),
];

/** Cached annotation UI layer ids per map; null means invalidated after styledata. */
const annotationUiLayerIdsCache = new WeakMap<object, string[] | null>();
const mapsWithStyleDataListener = new WeakSet<object>();

const installStyleDataListener = (map) => {
  if (!map?.on || mapsWithStyleDataListener.has(map)) return;
  mapsWithStyleDataListener.add(map);
  map.on("styledata", () => {
    annotationUiLayerIdsCache.set(map, null);
  });
};

/** Existing annotation UI layer ids (body + handles) present on the map style. */
export const annotationUiLayerIds = (map): string[] => {
  installStyleDataListener(map);
  const cached = annotationUiLayerIdsCache.get(map);
  if (cached != null) return cached;

  const styleLayers = map.getStyle?.()?.layers;
  const ids = !styleLayers
    ? []
    : styleLayers
        .map((layer) => layer.id)
        .filter(
          (id) =>
            id.startsWith(ANNOTATION_SOURCE_PREFIX) &&
            TOPMOST_UI_SUFFIXES.some((suffix) => id.endsWith(suffix))
        );
  annotationUiLayerIdsCache.set(map, ids);
  return ids;
};

/**
 * Annotation id of the topmost annotation body or handle under `point`, or null.
 * Uses Mapbox render order (queryRenderedFeatures returns top-to-bottom).
 * Handle features have no feature id; their owner is parsed from the source id.
 */
export const topmostAnnotationIdAt = (map, point): number | null => {
  const layers = annotationUiLayerIds(map);
  const feature = (
    layers.length > 0
      ? map.queryRenderedFeatures(point, { layers })
      : map.queryRenderedFeatures(point)
  ).find(
    (f) =>
      f.source?.startsWith(ANNOTATION_SOURCE_PREFIX) &&
      f.layer &&
      TOPMOST_UI_SUFFIXES.some((s) => f.layer.id.endsWith(s))
  );
  if (!feature) return null;

  if (HANDLE_LAYER_SUFFIXES.some((s) => feature.layer.id.endsWith(s))) {
    const match = feature.source?.match(
      new RegExp(`^${ANNOTATION_SOURCE_PREFIX}(\\d+)`)
    );
    return match ? Number(match[1]) : null;
  }
  return feature.id;
};

const configureFillPaintProperties = (
  color: string = "rgba(25, 25, 25, 1)",
  opacity: number = 0.3
) => {
  const fillColor = color;

  return {
    "fill-color": fillColor,
    "fill-opacity": opacity,
  };
};

const configureLinePaintProperties = (
  lineColor: string | null = "rgba(0, 240, 240, 1)",
  lineWidth: number | null = 1
) => {
  const hoverLineWidth = lineWidth + 1;
  const selectedLineWidth = lineWidth + 3;

  return {
    "line-color": [
      "case",
      ["boolean", ["feature-state", "hover"], false],
      "rgba(25, 25, 25, 1)",
      lineColor,
    ],
    "line-width": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      selectedLineWidth,
      [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        hoverLineWidth,
        lineWidth,
      ],
    ],
  };
};

export const getSourceFrom = (map) => (id) =>
  map.getSource(annotationSourceId(id));

export const addSourceTo = (map) => (id, options) => {
  const { geo, label } = options;
  const sourceId = annotationSourceId(id);

  map.addSource(sourceId, {
    type: "geojson",
    data: mainFeatureOf(geo, id),
  });

  map.addSource(centerSourceId(sourceId), {
    type: "geojson",
    data: centerFeatureOf(geo, id, label),
  });

  return map.getSource(sourceId);
};

export const addStrokeTo = (map) => (sourceId, options) => {
  const { paint } = options;

  map.addLayer({
    id: lineLayerId(sourceId),
    type: "line",
    source: sourceId,
    layout: {},
    paint: configureLinePaintProperties(paint.color, paint.line_width),
  });

  map.moveLayer(lineLayerId(sourceId));

  return map.getLayer(lineLayerId(sourceId));
};

const HIT_LINE_WIDTH = 12;

export const addStrokeHitAreaTo = (map) => (sourceId, options) => {
  const { paint } = options;

  map.addLayer({
    id: hitLayerId(sourceId),
    type: "line",
    source: sourceId,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "rgba(0, 0, 0, 1)",
      "line-opacity": 0,
      "line-width": Math.max(HIT_LINE_WIDTH, (paint.line_width ?? 1) + 8),
    },
  });
  map.moveLayer(hitLayerId(sourceId));
  return map.getLayer(hitLayerId(sourceId));
};

export const addFillTo = (map) => (sourceId, options) => {
  const { paint } = options;

  map.addLayer({
    id: fillLayerId(sourceId),
    type: "fill",
    source: sourceId,
    layout: {},
    paint: configureFillPaintProperties(paint.color, paint.opacity),
  });

  map.moveLayer(fillLayerId(sourceId));

  return map.getLayer(fillLayerId(sourceId));
};

export const addLabelTo = (map) => (sourceId) => {
  map.addLayer({
    id: labelLayerId(sourceId),
    source: centerSourceId(sourceId),
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-font": ["Roboto Bold", "Open Sans Bold"],
      "text-anchor": "center",
      "text-ignore-placement": true,
      "text-allow-overlap": true,
      "text-letter-spacing": 0.15,
      visibility: "none",
      "text-size": 20,
    },
    paint: {
      "text-color": "#e6e6e6",
      "text-halo-width": 2,
      "text-halo-color": "rgba(25, 25, 25, 1)",
      "text-halo-blur": 0,
    },
  });

  return map.getLayer(labelLayerId(sourceId));
};

export const addVertexHandlesTo = (map) => (sourceId, options) => {
  const { geo, paint } = options;
  const verticesSourceId = verticesLayerId(sourceId);

  map.addSource(verticesSourceId, {
    type: "geojson",
    data: vertexFeatureCollection(geo),
  });

  map.addLayer({
    id: verticesSourceId,
    type: "circle",
    source: verticesSourceId,
    layout: { visibility: "none" },
    paint: {
      "circle-radius": [
        "case",
        ["boolean", ["get", "selected"], false],
        7,
        5.5,
      ],
      "circle-color": [
        "case",
        ["boolean", ["get", "selected"], false],
        paint.color,
        "#ffffff",
      ],
      "circle-stroke-color": [
        "case",
        [
          "all",
          ["boolean", ["get", "selected"], false],
          ["boolean", ["get", "hovered"], false],
        ],
        "#000000",
        paint.color,
      ],
      "circle-stroke-width": 2,
    },
  });

  return map.getLayer(verticesSourceId);
};

export const addMidpointHandlesTo = (map) => (sourceId, options) => {
  const { geo } = options;
  const midpointsSourceId = midpointsLayerId(sourceId);

  map.addSource(midpointsSourceId, {
    type: "geojson",
    data: midpointFeatureCollection(geo),
  });

  map.addLayer({
    id: midpointsSourceId,
    type: "circle",
    source: midpointsSourceId,
    layout: { visibility: "none" },
    paint: {
      "circle-radius": 4,
      "circle-opacity": 0.55,
      "circle-color": "#ffffff",
      "circle-stroke-color": "#000000",
      "circle-stroke-width": 2,
    },
  });

  return map.getLayer(midpointsSourceId);
};

export const addCircleTo = (map) => (sourceId, options) => {
  map.addLayer({
    id: circleLayerId(sourceId),
    source: centerSourceId(sourceId),
    type: "circle",
    paint: {
      "circle-radius": options.paint.circle_radius || 3,
      "circle-color": options.paint.color || "#00E5EE",
      "circle-opacity": options.paint.opacity || 1,
      "circle-stroke-color": options.paint.stroke_color || "#000",
      "circle-stroke-width": options.paint.stroke_width || 0,
    },
  });

  return map.getLayer(circleLayerId(sourceId));
};

export const moveLabelsOn = (map) => {
  const getSource = getSourceFrom(map);

  return (ids: number[]) => {
    if (!map) return;
    for (const id of ids) {
      const source = getSource(id);
      if (source) {
        const layer = map.getLayer(labelLayerId(source.id));
        if (layer) {
          map.moveLayer(labelLayerId(source.id));
        }
      }
    }
  };
};

export const moveOutlineOn = (map) => {
  const getSource = getSourceFrom(map);

  return (ids: number[]) => {
    if (!map) return;
    for (const id of ids) {
      const source = getSource(id);
      if (source) {
        const layer = map.getLayer(lineLayerId(source.id));
        if (layer) {
          map.moveLayer(lineLayerId(source.id));
        }
      }
    }
  };
};
